class ScrollRestoration {
  static STORAGE_KEY_PREFIX = 'scroll:';
  static SCROLL_TOLERANCE_PX = 1;
  static SETTLE_SHORTFALL_PX = 200;
  static EARLY_RESTORE_DEADLINE_MS = 2500;
  static MAX_DEADLINE_REARMS = 4;
  static MAX_RESTORE_Y = 1000000;
  static USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown'];

  static generateEntryId() {
    return Date.now().toString(36) + Math.random().toString(36).slice(2);
  }

  static readEntryId(state) {
    return (
      (typeof state === 'object' ? state?.scrollRestorationId : null) || null
    );
  }

  static mergeEntryId(state, entryId) {
    const nextState = state && typeof state === 'object' ? { ...state } : {};
    nextState.scrollRestorationId = entryId;
    return nextState;
  }

  constructor() {
    this.html = document.documentElement;
    this.storageKey = null;
    this.targetY = null;
    this.reservation = null;
    this.frameId = 0;
    this.deadlineId = 0;
    this.deadlineRearms = 0;
    this.settleUserScrolled = false;

    this.originalPushState = history.pushState;
    this.originalReplaceState = history.replaceState;

    this.attempt = this.attempt.bind(this);
    this.onReadyStateChange = this.onReadyStateChange.bind(this);
    this.onUserScroll = this.onUserScroll.bind(this);
    this.onDeadline = this.onDeadline.bind(this);
    this.removeUserScrollListeners = this.removeUserScrollListeners.bind(this);
    this.onPopState = this.onPopState.bind(this);
    this.onPageHide = this.onPageHide.bind(this);
    this.onDomReady = this.onDomReady.bind(this);
    this.onSettleUserScroll = this.onSettleUserScroll.bind(this);
    this.onLoad = this.onLoad.bind(this);
    this.settle = this.settle.bind(this);
  }

  init() {
    this.deriveStorageKey();
    this.watchHistoryEntry();
    window.addEventListener('pagehide', this.onPageHide);

    if (!this.isBackForwardNavigation()) return;

    this.html.classList.add('is-back-forward-nav');
    this.disableNativeScrollRestoration();

    this.targetY = this.readSavedScrollY();
    this.whenDomReady(this.onDomReady);

    if (this.targetY === null) return;

    this.html.dataset.scrollRestore = 'pending';
    this.reserveHeight();
    this.listenForUserScroll();

    document.addEventListener('readystatechange', this.onReadyStateChange);

    this.deadlineId = window.setTimeout(
      this.onDeadline,
      ScrollRestoration.EARLY_RESTORE_DEADLINE_MS
    );
    this.frameId = window.requestAnimationFrame(this.attempt);
  }

  deriveStorageKey() {
    try {
      let entryId = ScrollRestoration.readEntryId(history.state);

      if (!entryId) {
        entryId = ScrollRestoration.generateEntryId();
        this.originalReplaceState.call(
          history,
          ScrollRestoration.mergeEntryId(history.state, entryId),
          ''
        );
      }

      this.setStorageKey(entryId);
    } catch {}
  }

  setStorageKey(entryId) {
    this.storageKey = ScrollRestoration.STORAGE_KEY_PREFIX + entryId;

    try {
      this.html.dataset.scrollRestoreKey = this.storageKey;
    } catch {}
  }

  currentEntryId() {
    return this.storageKey
      ? this.storageKey.slice(ScrollRestoration.STORAGE_KEY_PREFIX.length)
      : null;
  }

  watchHistoryEntry() {
    const restoration = this;

    history.pushState = function (state, title, url) {
      const entryId = ScrollRestoration.generateEntryId();
      const result = restoration.originalPushState.call(
        this,
        ScrollRestoration.mergeEntryId(state, entryId),
        title,
        url
      );
      restoration.setStorageKey(entryId);
      return result;
    };

    history.replaceState = function (state, title, url) {
      const entryId = restoration.currentEntryId();
      const nextState = entryId
        ? ScrollRestoration.mergeEntryId(state, entryId)
        : state;
      return restoration.originalReplaceState.call(this, nextState, title, url);
    };

    window.addEventListener('popstate', this.onPopState);
  }

  onPopState(event) {
    let entryId = ScrollRestoration.readEntryId(event.state);

    if (!entryId) {
      entryId = ScrollRestoration.generateEntryId();

      try {
        this.originalReplaceState.call(
          history,
          ScrollRestoration.mergeEntryId(event.state, entryId),
          ''
        );
      } catch {
        this.storageKey = null;
        return;
      }
    }

    this.setStorageKey(entryId);
  }

  onPageHide() {
    if (!this.storageKey) return;

    try {
      sessionStorage.setItem(this.storageKey, String(window.scrollY));
    } catch {}
  }

  isBackForwardNavigation() {
    try {
      const navigationEntry = performance.getEntriesByType('navigation')[0];
      return navigationEntry?.type === 'back_forward';
    } catch {
      return false;
    }
  }

  disableNativeScrollRestoration() {
    try {
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }
    } catch {}
  }

  enableNativeScrollRestoration() {
    try {
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'auto';
      }
    } catch {}
  }

  readSavedScrollY() {
    try {
      if (!this.storageKey) return null;

      const savedScrollY = sessionStorage.getItem(this.storageKey);
      if (savedScrollY === null) return null;

      const parsedScrollY = Number.parseInt(savedScrollY, 10);
      if (Number.isNaN(parsedScrollY) || parsedScrollY <= 0) return null;

      return Math.min(parsedScrollY, ScrollRestoration.MAX_RESTORE_Y);
    } catch {
      return null;
    }
  }

  reserveHeight() {
    this.reservation = document.createElement('style');
    this.reservation.textContent =
      'html[data-scroll-restore="pending"] body { min-height: calc(' +
      this.targetY +
      'px + 100vh); }';
    document.head.appendChild(this.reservation);
  }

  listenForUserScroll() {
    for (const eventName of ScrollRestoration.USER_SCROLL_EVENTS) {
      window.addEventListener(eventName, this.onUserScroll, {
        passive: true,
      });
    }

    document.addEventListener(
      'DOMContentLoaded',
      this.removeUserScrollListeners,
      { once: true }
    );
  }

  removeUserScrollListeners() {
    for (const eventName of ScrollRestoration.USER_SCROLL_EVENTS) {
      window.removeEventListener(eventName, this.onUserScroll);
    }
  }

  scrollToInstantly(y) {
    const previousScrollBehavior = this.html.style.scrollBehavior;

    this.html.style.scrollBehavior = 'auto';
    window.scrollTo(0, y);
    this.html.style.scrollBehavior = previousScrollBehavior;
  }

  reachedTarget() {
    return (
      window.scrollY >= this.targetY - ScrollRestoration.SCROLL_TOLERANCE_PX
    );
  }

  releaseReservation() {
    if (this.frameId) {
      window.cancelAnimationFrame(this.frameId);
      this.frameId = 0;
    }

    if (this.deadlineId) {
      window.clearTimeout(this.deadlineId);
      this.deadlineId = 0;
    }

    document.removeEventListener('readystatechange', this.onReadyStateChange);

    if (this.reservation) {
      this.reservation.remove();
      this.reservation = null;
    }
  }

  finish() {
    this.releaseReservation();

    if (this.html.dataset.scrollRestore === 'aborted') return;

    this.scrollToInstantly(this.targetY);
    this.html.dataset.scrollRestore = this.reachedTarget()
      ? 'done'
      : 'unreached';
  }

  attempt() {
    this.frameId = 0;

    if (document.body) {
      const reachableY = this.html.scrollHeight - window.innerHeight;

      if (reachableY >= this.targetY - ScrollRestoration.SCROLL_TOLERANCE_PX) {
        this.scrollToInstantly(this.targetY);
        if (this.reachedTarget()) return;
      }
    }

    this.frameId = window.requestAnimationFrame(this.attempt);
  }

  onReadyStateChange() {
    if (document.readyState === 'loading') return;
    this.finish();
  }

  onUserScroll() {
    this.removeUserScrollListeners();
    this.html.dataset.scrollRestore = 'aborted';
    this.releaseReservation();
  }

  onDeadline() {
    this.deadlineId = 0;

    if (document.readyState === 'loading') {
      if (this.deadlineRearms < ScrollRestoration.MAX_DEADLINE_REARMS) {
        this.deadlineRearms += 1;
        this.deadlineId = window.setTimeout(
          this.onDeadline,
          ScrollRestoration.EARLY_RESTORE_DEADLINE_MS
        );
        return;
      }

      this.releaseReservation();
      this.html.dataset.scrollRestore = 'unreached';
      return;
    }

    this.finish();
  }

  whenDomReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }

    callback();
  }

  onDomReady() {
    const earlyRestore = this.html.dataset.scrollRestore;

    if (earlyRestore === 'aborted') {
      this.enableNativeScrollRestoration();
      return;
    }

    if (this.targetY !== null) {
      if (earlyRestore !== 'done') this.scrollToInstantly(this.targetY);
      this.scheduleSettledReapply();
    }

    this.enableNativeScrollRestoration();
  }

  scheduleSettledReapply() {
    for (const eventName of ScrollRestoration.USER_SCROLL_EVENTS) {
      window.addEventListener(eventName, this.onSettleUserScroll, {
        passive: true,
      });
    }

    if (document.readyState === 'complete') {
      this.onLoad();
      return;
    }

    window.addEventListener('load', this.onLoad, { once: true });
  }

  onSettleUserScroll() {
    this.settleUserScrolled = true;
  }

  onLoad() {
    window.requestAnimationFrame(this.settle);
  }

  settle() {
    for (const eventName of ScrollRestoration.USER_SCROLL_EVENTS) {
      window.removeEventListener(eventName, this.onSettleUserScroll);
    }

    if (this.html.dataset.scrollRestore === 'aborted') return;

    if (
      !this.settleUserScrolled &&
      window.scrollY < this.targetY - ScrollRestoration.SETTLE_SHORTFALL_PX
    ) {
      this.scrollToInstantly(this.targetY);
    }
  }
}

new ScrollRestoration().init();
